import { zodResolver } from '@hookform/resolvers/zod';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { useAcceptCharger, useCreateCharger, type Charger } from '../../api/chargers';
import { useOrganisations, useSites } from '../../api/organisations';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { FormDialog } from '../../components/dialogs/FormDialog';
import { applyApiError } from '../../components/form/apiErrors';
import { FieldRow } from '../../components/form/FieldRow';
import { FormSelect } from '../../components/form/FormSelect';
import { FormTextField } from '../../components/form/FormTextField';
import { useToast } from '../../components/Toast';
import { formSaver, useUnsavedChanges } from '../../components/unsaved/unsaved';

const schema = z.object({
  chargePointId: z.string().trim(),
  connectorCount: z.string(),
  organisationId: z.string().min(1, 'Choose an organisation'),
  siteId: z.string().min(1, 'Choose a site'),
  label: z.string().trim().max(64),
  bay: z.string().trim().max(32),
});
type Values = z.infer<typeof schema>;

/**
 * Puts a charger on a site so it can charge. With a charger: one that connected for the first
 * time. Without: a charger added by hand before it's installed, accepted the moment it connects.
 */
export function AcceptChargerDialog({ charger, close }: { charger?: Charger } & DialogProps<Charger>) {
  const accept = useAcceptCharger();
  const create = useCreateCharger();
  const orgs = useOrganisations();
  const toast = useToast();
  const [error, setError] = useState('');
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { chargePointId: '', connectorCount: '1', organisationId: '', siteId: '', label: '', bay: '' } });
  const organisationId = useWatch({ control: form.control, name: 'organisationId' });
  const sites = useSites(organisationId || undefined);
  const siteOptions = useMemo(() => (organisationId ? (sites.data ?? []).map((s) => ({ value: s.id, label: s.name })) : []), [organisationId, sites.data]);
  // A different organisation means choosing its site afresh; with only one site, it's that one.
  useEffect(() => form.setValue('siteId', siteOptions.length === 1 ? siteOptions[0]!.value : ''), [organisationId, siteOptions, form]);

  const submit = formSaver(form, async (v) => {
    setError('');
    try {
      if (charger) {
        const saved = await accept.mutateAsync({ id: charger.id, siteId: v.siteId, label: v.label || undefined, bay: v.bay || undefined });
        toast(`${saved.label ?? saved.chargePointId} was accepted.${charger.isConnected ? ' It will start working within a minute.' : ''}`);
        close(saved);
        return;
      }
      if (!v.chargePointId) {
        form.setError('chargePointId', { message: 'Enter the charge point ID' });
        return false;
      }
      const saved = await create.mutateAsync({ chargePointId: v.chargePointId, siteId: v.siteId, label: v.label || undefined, bay: v.bay || undefined, connectorCount: Number(v.connectorCount) });
      toast(
        saved.wasWaiting
          ? `${saved.label ?? saved.chargePointId} had already connected, and is now accepted.`
          : `${saved.label ?? saved.chargePointId} was added. It can charge as soon as it connects.`,
      );
      close(saved);
    } catch (e) {
      setError(applyApiError(e, form.setError, ['chargePointId', 'connectorCount', 'siteId', 'label', 'bay']));
      return false;
    }
  });
  useUnsavedChanges({ dirty: form.formState.isDirty, save: submit, discard: () => form.reset() });

  return (
    <FormDialog
      title={charger ? `Accept ${charger.chargePointId}` : 'Add a charger'}
      lead={
        charger ? (
          <>{[charger.vendor, charger.model].filter(Boolean).join(' ') || 'A charger'} connected for the first time. Choose where it is, and it can start charging.</>
        ) : (
          <>For a charger that hasn't connected yet. When it connects with this ID, it can charge straight away.</>
        )
      }
      submitLabel={charger ? 'Accept the charger' : 'Add the charger'}
      onSubmit={submit}
      onClose={() => close()}
      busy={accept.isPending || create.isPending}
      error={error}
    >
      {!charger && (
        <FieldRow>
          <FormTextField
            control={form.control}
            name="chargePointId"
            label="Charge point ID"
            placeholder="e.g. SCH-RICH-04"
            helperText="As set on the charger: the part after wss://ocpp.infinityev.com.au/"
            autoFocus
            slotProps={{ htmlInput: { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } }}
          />
          <FormSelect control={form.control} name="connectorCount" label="Sockets" options={[1, 2, 3, 4].map((n) => ({ value: String(n), label: String(n) }))} />
        </FieldRow>
      )}
      <FormSelect
        control={form.control}
        name="organisationId"
        label="Organisation"
        options={(orgs.data ?? []).map((o) => ({ value: o.id, label: o.businessName }))}
      />
      {/* Most organisations have one site: then there's nothing to choose. */}
      {(siteOptions.length > 1 || (organisationId && siteOptions.length === 0 && !sites.isPending)) && (
        <FormSelect control={form.control} name="siteId" label="Site" options={siteOptions} helperText={siteOptions.length === 0 ? 'This organisation has no sites yet: add one under Settings, Organisations' : undefined} />
      )}
      <FieldRow>
        <FormTextField control={form.control} name="label" label="Name (optional)" placeholder="e.g. Car park east" />
        <FormTextField control={form.control} name="bay" label="Bay (optional)" placeholder="e.g. 3" />
      </FieldRow>
      {charger && (
        <Typography variant="body2" color="textSecondary">
          Until you accept it, it can connect but not charge. Reject it if you don't recognise it.
        </Typography>
      )}
    </FormDialog>
  );
}
